import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";
import { toast } from "sonner";
import { ArrowRight, Loader2, Phone, ChevronLeft } from "lucide-react";

// Google social login via Emergent-managed Auth.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function AuthProviders() {
    const { applyAuthData } = useApp();
    const navigate = useNavigate();
    const [mode, setMode] = useState("buttons"); // buttons | phone
    const [step, setStep] = useState(1);
    const [phone, setPhone] = useState("");
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);

    const sendOtp = async () => {
        const digits = phone.replace(/\D/g, "").slice(0, 10);
        if (digits.length !== 10) { toast.error("Enter a valid 10-digit mobile number"); return; }
        setLoading(true);
        try {
            await api.post("/auth/otp/send", { phone: "+91" + digits });
            setStep(2);
            toast.success("OTP sent to +91 " + digits);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    const verifyOtp = async () => {
        setLoading(true);
        try {
            const digits = phone.replace(/\D/g, "").slice(0, 10);
            const { data } = await api.post("/auth/otp/verify", { phone: "+91" + digits, code });
            applyAuthData(data);
            toast.success("Welcome!");
            let dest = "/onboarding";
            try { if ((await api.get("/my/business")).data) dest = "/dashboard"; } catch {}
            navigate(dest);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <div className="mt-5">
            <div className="flex items-center gap-3 my-2">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">or continue with</span>
                <div className="h-px flex-1 bg-border" />
            </div>

            {mode === "buttons" && (
                <div className="mt-4 space-y-2.5">
                    <Button type="button" variant="ghost" onClick={startGoogleLogin} className="glass rounded-full w-full h-11 justify-center" data-testid="google-login-btn">
                        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" /> Continue with Google
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => { setMode("phone"); setStep(1); }} className="glass rounded-full w-full h-11 justify-center" data-testid="phone-login-btn">
                        <Phone className="w-4 h-4 mr-2" /> Continue with Phone (OTP)
                    </Button>
                </div>
            )}

            {mode === "phone" && (
                <div className="mt-4 space-y-3" data-testid="phone-otp-form">
                    <button type="button" onClick={() => { setMode("buttons"); setStep(1); setCode(""); }} className="text-xs text-muted-foreground inline-flex items-center gap-1"><ChevronLeft className="w-3.5 h-3.5" /> Back</button>
                    {step === 1 ? (
                        <>
                            <Label className="text-sm">Mobile number</Label>
                            <div className="flex items-center rounded-xl border border-input bg-background px-3 h-11">
                                <span className="text-sm text-muted-foreground select-none">+91</span>
                                <input inputMode="numeric" value={phone.replace(/\D/g, "").slice(0, 10)} maxLength={10}
                                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                    placeholder="90000 00000" className="flex-1 bg-transparent outline-none px-2 text-sm" data-testid="otp-phone-input" />
                            </div>
                            <Button type="button" onClick={sendOtp} disabled={loading} className="btn-accent rounded-full w-full h-11" data-testid="otp-send-btn">
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send OTP"}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Label className="text-sm">Enter the 6-digit OTP</Label>
                            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" className="rounded-xl h-11 tracking-[0.4em] text-center" data-testid="otp-code-input" />
                            <Button type="button" onClick={verifyOtp} disabled={loading || code.length < 4} className="btn-accent rounded-full w-full h-11" data-testid="otp-verify-btn">
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Continue"}
                            </Button>
                            <button type="button" onClick={sendOtp} disabled={loading} className="text-xs text-accent w-full text-center">Resend OTP</button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function AuthShell({ title, subtitle, children, footer }) {
    return (
        <div className="relative min-h-screen flex items-center justify-center px-4 py-16">
            <Link to="/" className="absolute top-6 left-6 flex items-center gap-2.5" data-testid="auth-logo">
                <div className="w-7 h-7 rounded-full sphere" />
                <span className="font-heading text-sm font-semibold uppercase tracking-[0.22em]">Bizlinkk</span>
            </Link>
            <div className="absolute top-5 right-5"><LanguageSwitcher variant="ghost" testid="auth-lang" /></div>
            <div className="w-full max-w-md glass-strong rounded-3xl p-8 sm:p-10 fade-up">
                <h1 className="font-heading text-2xl font-semibold tracking-tight">{title}</h1>
                <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
                {children}
                <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>
            </div>
        </div>
    );
}

export function LoginPage() {
    const { login } = useApp();
    const { t } = useI18n();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success(t("login_welcome_toast"));
            navigate("/dashboard");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell title={t("login_title")} subtitle={t("login_sub")}
            footer={<>{t("no_account")} <Link to="/signup" className="text-accent font-medium" data-testid="to-signup-link">{t("create_website")}</Link></>}>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
                <div>
                    <Label htmlFor="email">{t("field_email")}</Label>
                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        className="mt-1.5 rounded-xl" placeholder="you@example.com" data-testid="login-email-input" />
                </div>
                <div>
                    <Label htmlFor="password">{t("field_password")}</Label>
                    <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                        className="mt-1.5 rounded-xl" placeholder="••••••••" data-testid="login-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="btn-accent rounded-full w-full h-11" data-testid="login-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("login_cta")} <ArrowRight className="ml-1 w-4 h-4" /></>}
                </Button>
            </form>
            <AuthProviders />
        </AuthShell>
    );
}

export function SignupPage() {
    const { register } = useApp();
    const { t } = useI18n();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        setLoading(true);
        try {
            await register(name, email, password);
            toast.success(t("signup_created_toast"));
            navigate("/onboarding");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthShell title={t("signup_title")} subtitle={t("signup_sub")}
            footer={<>{t("have_account")} <Link to="/login" className="text-accent font-medium" data-testid="to-login-link">{t("login")}</Link></>}>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="signup-form">
                <div>
                    <Label htmlFor="name">{t("field_name")}</Label>
                    <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
                        className="mt-1.5 rounded-xl" placeholder={t("placeholder_name")} data-testid="signup-name-input" />
                </div>
                <div>
                    <Label htmlFor="email">{t("field_email")}</Label>
                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        className="mt-1.5 rounded-xl" placeholder="you@example.com" data-testid="signup-email-input" />
                </div>
                <div>
                    <Label htmlFor="password">{t("field_password")}</Label>
                    <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                        className="mt-1.5 rounded-xl" placeholder={t("placeholder_password_min")} data-testid="signup-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="btn-accent rounded-full w-full h-11" data-testid="signup-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("signup_cta")} <ArrowRight className="ml-1 w-4 h-4" /></>}
                </Button>
            </form>
            <AuthProviders />
        </AuthShell>
    );
}
